import type JSZip from "jszip";
import {
  type FilteredLookalikeAudience,
  type LookalikeAudienceStatisticsFileStructure,
} from "features/mediaDataRoom/models";
import { type MediaDataRoomJobResultTransform } from "../useMediaDataRoomJob/models";

// Detailed calculation description:
// https://www.notion.so/decentriq/Re-Enable-Beta-Model-Quality-Viz-3-18-2-b49a6106201448aaac1d3c11568910b8?pvs=4#dd6bf187d4f74c4db7c18f4dcba677b0
export const calculateAudienceQualityScore = (
  filteredAudiences: FilteredLookalikeAudience[],
  selectedReach: number
): number => {
  const trueFalsePositivePairs: number[][] = (filteredAudiences || []).map(
    ({ quality: { roc_curve } }) => [roc_curve?.tpr, roc_curve?.fpr]
  );
  const reachValues: number[] = (filteredAudiences || []).map(
    ({ reach }) => reach
  );
  const normalizedPrecision = trueFalsePositivePairs.map(
    ([tpr, fpr]) => tpr / (tpr + fpr)
  );
  const maxNormalizedPrecision = Math.max(...normalizedPrecision);
  const offset = 0.5;
  const scaleFactor =
    maxNormalizedPrecision > offset
      ? (1 - offset) / (maxNormalizedPrecision - offset)
      : 1;
  // Calculate quality
  const quality = normalizedPrecision.map(
    // Make sure we don't get negative quality scores, instead make sure
    // such raw quality scores would be 0.5.
    (precision) =>
      scaleFactor * (precision > offset ? precision - offset : 0) + offset
  );
  // Pair slider position with quality
  const qualityPerReachArray = reachValues.map((reach, index) => [
    quality[index],
    reach,
  ]);
  const [rawQualityScore = 0] =
    qualityPerReachArray.find(([, reach]) => reach === selectedReach) || [];
  return rawQualityScore === 1
    ? rawQualityScore * 10
    : parseFloat((rawQualityScore * 10).toFixed(2));
};

export const roundExcludedAudienceSize = (size: number) =>
  Math.round(size / 100) * 100;

export const transform: MediaDataRoomJobResultTransform<
  LookalikeAudienceStatisticsFileStructure
> = async (zip: JSZip) => {
  const audienceStatisticsFile = zip.file("lookalike_audience.json");
  if (audienceStatisticsFile === null) {
    throw new Error("lookalike_audience.json not found in zip");
  }
  const audienceStatisticsFileStructure = JSON.parse(
    await audienceStatisticsFile.async("string")
  ) as LookalikeAudienceStatisticsFileStructure;
  return audienceStatisticsFileStructure;
};
